<template>
  <div>
    <b-row>
      <!-- Filter sidebar column -->
      <b-col
        class="d-none d-lg-block"
        cols="2"
      >
        <product-filter
          :filter-display="filterDisplay"
          :filter="{}"
          :constraints="{ dateType: 'None', dateTo: '', dateFrom: '' }"
        />
      </b-col>

      <!-- Main content column -->
      <b-col cols="12" lg="10">
        <!-- Recently Viewed -->
        <div class="mb-2">
          <h2 class="mb-1">Recently Viewed Products</h2>
          <swiper :options="swiperOptions" class="swiper-container">
            <swiper-slide
              v-for="(card, index) in recentlyViewed"
              :key="index"
            >
              <b-card class="mb-0 py-0">
                <img
                  :src="`${baseURL}/servethumbnail.cfm?id=${card.detailsId}`"
                  class="card-img-top"
                  alt="Product thumbnail"
                >
                <b-card-body>
                  <b-card-title class="mb-0">Report {{ card.reportnumber }}</b-card-title>
                  <b-card-text class="mb-1">{{ card.provider }}</b-card-text>
                </b-card-body>
              </b-card>
            </swiper-slide>
            <div slot="button-prev" class="swiper-button-prev" />
            <div slot="button-next" class="swiper-button-next" />
          </swiper>
        </div>

        <!-- Header -->
        <section class="mb-1">
          <h2 class="mb-0">Full Structured Product Database</h2>
          <h5 class="text-muted mt-50 mb-50">{{ totalRows }} results</h5>
          <div>
            <b-button variant="primary" disabled class="mr-1">Analyse Results</b-button>
            <b-button variant="primary" disabled>Add To Portfolio</b-button>
          </div>
        </section>

        <!-- Searchbar -->
        <b-input-group class="input-group-merge mb-2">
          <b-form-input
            placeholder="Search (ID, name or identifier)"
            disabled
          />
          <b-input-group-append is-text>
            <feather-icon icon="SearchIcon" class="text-muted" />
          </b-input-group-append>
        </b-input-group>

        <!-- Table -->
        <b-card no-body>
          <b-table
            :items="rows"
            :fields="fields"
            responsive
            hover
          >
            <template #cell(producttype)="data">
              <div class="d-flex align-items-center">
                <b-avatar class="mr-1" :variant="data.item.iconColor">
                  <feather-icon size="18" :icon="data.item.iconName" />
                </b-avatar>
                <span>{{ data.item.producttype }}</span>
              </div>
            </template>
            <template #cell(portfolio)>
              <b-button
                variant="light-success"
                disabled
                class="btn-icon rounded-circle p-50"
              >
                <feather-icon icon="PlusIcon" size="14" />
              </b-button>
            </template>
          </b-table>

          <!-- Pagination -->
          <div class="d-flex justify-content-between px-2 my-2">
            <div>
              <span class="pb-1">Rows Per Page</span>
              <b-form-select
                :value="20"
                :options="[10, 20, 50]"
                disabled
                class="d-inline-block ml-1"
                style="width: auto"
              />
            </div>
            <b-pagination
              :value="1"
              :total-rows="totalRows"
              :per-page="20"
              align="center"
              size="lg"
              class="mb-0"
              disabled
            />
          </div>
        </b-card>

      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardTitle,
  BCardText,
  BTable,
  BAvatar,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BFormInput,
  BPagination,
  BFormSelect,
} from 'bootstrap-vue'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import ProductFilter from '@/views/components/ProductFilter.vue'

const RECENTLY_VIEWED = [
  {
    reporttitle: 'FTSE 150 Kick Out Plan August 2018 - Option 2',
    detailsId: 5554,
    provider: 'Mariana',
    reportnumber: 4826,
  },
  {
    reporttitle: 'UK & US Step Down Kick-out Plan (MS231)',
    detailsId: 10628,
    provider: 'Walker Crips',
    reportnumber: 9509,
  },
  {
    reporttitle: 'Europe Annual Kick-out Plan (HS685)',
    detailsId: 10635,
    provider: 'Walker Crips',
    reportnumber: 9497,
  },
  {
    reporttitle: 'Callable Growth Deposit Plan - Issue 26 - February 2026',
    detailsId: 10550,
    provider: 'IDAD',
    reportnumber: 9419,
  },
  {
    reporttitle: 'Japan Step Down Kick-out Plan (HS643)',
    detailsId: 10331,
    provider: 'Walker Crips',
    reportnumber: 9253,
  },
  {
    reporttitle: 'FTSE Monthly Fixed Income November 2025 | NA10177',
    detailsId: 10388,
    provider: 'Meteor',
    reportnumber: 9283,
  },
  {
    reporttitle: 'UK 4Y Fixed Annual Income Deposit Plan November 2022 - 5654',
    detailsId: 7697,
    provider: 'MB Structured Investments',
    reportnumber: 6829,
  },
  {
    reporttitle: 'FTSE 100 EWFD Long Kick-Out Plan December 2022 - Option 1',
    detailsId: 7681,
    provider: 'Tempo Structured Products',
    reportnumber: 6836,
  },
  {
    reporttitle: '7Y US & Europe Defensive Step-Down 70 Kickout Plan (CA37)',
    detailsId: 10639,
    provider: 'Arcus Partners',
    reportnumber: 9507,
  },
  {
    reporttitle: '7Y Europe Monthly Conditional Income 85 Kickout Plan (CIBC09)',
    detailsId: 10630,
    provider: 'hop investing',
    reportnumber: 9492,
  },
]

const TYPES = [
  { label: 'Defensive Autocall', icon: 'TrendingUpIcon', color: 'light-primary' },
  { label: 'Growth Autocall', icon: 'TrendingUpIcon', color: 'light-primary' },
  { label: 'Capital Protected', icon: 'ShieldIcon', color: 'light-success' },
  { label: 'Digital Growth', icon: 'ActivityIcon', color: 'light-warning' },
  { label: 'Fixed Income', icon: 'DollarSignIcon', color: 'light-info' },
]

const NAMES = [
  'FTSE 100 Step-Down Autocall 2024',
  'S&P 500 Capital Protected Note',
  'EURO STOXX 50 Barrier Note',
  'FTSE 100 / S&P 500 Dual Index',
  'Nasdaq Growth Accelerator',
  'FTSE 100 Enhanced Kick-Out',
  'Global Equity Defensive Note',
  'Multi-Asset Autocall Series 3',
  'UK Blue Chip Income Plan',
  'S&P 500 Defensive Growth Plan',
  'FTSE 250 Step-Down Plan',
  'Euro High Yield Autocall',
  'Asia Pacific Tracker Note',
  'Investment Grade Bond Linked',
  'Commodity Linked Capital Note',
  'Developed Markets Barrier Note',
  'Russell 2000 Kick-Out Plan',
  'Hang Seng Capital Protected',
  'Global Dividend Income Note',
  'CAC 40 Autocall Series 7',
]

const PROVIDERS = ['Barclays', 'Goldman Sachs', 'Morgan Stanley', 'HSBC', 'Investec', 'BNP Paribas', 'Societe Generale', 'Meteor']

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function fakeDate(yearOffset, monthOffset) {
  const d = new Date(2022, 0, 1)
  d.setFullYear(d.getFullYear() + yearOffset)
  d.setMonth(d.getMonth() + monthOffset)
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

const FILTER_DISPLAY = [
  {
    categoryLabel: 'Status',
    categoryKey: 'status',
    items: [
      {
        pinned: true,
        label: 'Current',
        count: 81,
        value: 0,
      },
      {
        pinned: false,
        label: 'Live',
        count: 2132,
        value: 1,
      },
      {
        pinned: false,
        label: 'Matured',
        count: 5738,
        value: 2,
      },
    ],
  },
  {
    categoryLabel: 'Capital',
    categoryKey: 'capital',
    items: [
      {
        pinned: true,
        label: 'At risk',
        count: 5208,
        value: 2,
      },
      {
        pinned: false,
        label: 'Partially protected',
        count: 5,
        value: 3,
      },
      {
        pinned: false,
        label: 'Protected',
        count: 1154,
        value: 1,
      },
    ],
  },
  {
    categoryLabel: 'Aim',
    categoryKey: 'aim',
    items: [
      {
        pinned: true,
        label: 'Growth',
        count: 5275,
        value: 0,
      },
      {
        pinned: false,
        label: 'Income',
        count: 1092,
        value: 1,
      },
    ],
  },
  {
    categoryLabel: 'Vehicle',
    categoryKey: 'vehicle',
    items: [
      {
        pinned: true,
        label: 'Deposit',
        count: 1054,
        value: 2,
      },
      {
        pinned: true,
        label: 'Fund',
        count: 4,
        value: 4,
      },
      {
        pinned: false,
        label: 'Plan',
        count: 5305,
        value: 1,
      },
      {
        pinned: false,
        label: 'Security',
        count: 5,
        value: 3,
      },
    ],
  },
  {
    categoryLabel: 'Maturity',
    categoryKey: 'maturity',
    slider: {
      suffix: 'y',
      customLabels: [],
      direction: 'ltr',
      items: [2, 3, 4, 5, 6, 7, 8, 9, 10],
    },
    items: [
      {
        pinned: true,
        label: '2 y',
        count: 37,
        value: 2,
      },
      {
        pinned: true,
        label: '3 y',
        count: 266,
        value: 3,
      },
      {
        pinned: true,
        label: '4 y',
        count: 198,
        value: 4,
      },
      {
        pinned: false,
        label: '5 y',
        count: 1626,
        value: 5,
      },
      {
        pinned: false,
        label: '6 y',
        count: 3731,
        value: 6,
      },
      {
        pinned: false,
        label: '7 y',
        count: 888,
        value: 7,
      },
      {
        pinned: false,
        label: '8 y',
        count: 381,
        value: 8,
      },
      {
        pinned: false,
        label: '9 y',
        count: 45,
        value: 9,
      },
      {
        pinned: false,
        label: '10 y',
        count: 779,
        value: 10,
      },
    ],
  },
  {
    categoryLabel: 'Maturity Type',
    categoryKey: 'maturitytype',
    items: [
      {
        pinned: true,
        label: 'Fixed',
        count: 1186,
        value: 0,
      },
      {
        pinned: false,
        label: 'Variable',
        count: 5181,
        value: 1,
      },
    ],
  },
  {
    categoryLabel: 'Product Type',
    categoryKey: 'producttype',
    items: [
      {
        pinned: true,
        label: 'Defensive Autocall',
        count: 2865,
        value: 3,
      },
      {
        pinned: true,
        label: 'Growth Autocall',
        count: 1594,
        value: 2,
      },
      {
        pinned: true,
        label: 'Income Autocall',
        count: 657,
        value: 6,
      },
      {
        pinned: true,
        label: 'Fixed Rate Callable',
        count: 81,
        value: 16,
      },
      {
        pinned: false,
        label: 'Digital Growth',
        count: 501,
        value: 4,
      },
      {
        pinned: false,
        label: 'Protected Growth',
        count: 144,
        value: 1,
      },
      {
        pinned: false,
        label: 'Fixed Income',
        count: 307,
        value: 5,
      },
    ],
  },
  {
    categoryLabel: 'Barrier Types',
    categoryKey: 'barriertypes',
    items: [
      {
        pinned: true,
        label: 'Capital protected',
        count: 1134,
        value: 0,
      },
      {
        pinned: false,
        label: 'Daily levels (American)',
        count: 202,
        value: 3,
      },
      {
        pinned: false,
        label: 'Final day (European)',
        count: 5031,
        value: 2,
      },
    ],
  },
  {
    categoryLabel: 'Underlying',
    categoryKey: 'underlying',
    items: [
      {
        pinned: true,
        label: 'FTSE 100 Index',
        count: 6795,
        value: 1,
      },
      {
        pinned: true,
        label: 'EURO STOXX 50 Index',
        count: 1007,
        value: 4,
      },
      {
        pinned: true,
        label: 'S&P-500 Index',
        count: 710,
        value: 2255,
      },
      {
        pinned: false,
        label: 'Nikkei 225 Index',
        count: 41,
        value: 3,
      },
      {
        pinned: false,
        label: 'NASDAQ-100 Index',
        count: 1,
        value: 1005,
      },
    ],
  },
  {
    categoryLabel: 'Underlying Count',
    categoryKey: 'underlyings',
    items: [
      {
        pinned: true,
        label: 'Single',
        count: 6289,
        value: 'Single',
      },
      {
        pinned: false,
        label: 'Multiple',
        count: 1662,
        value: 'Multiple',
      },
    ],
  },
  {
    categoryLabel: 'Providers',
    categoryKey: 'providers',
    items: [
      {
        pinned: true,
        label: 'Barclays',
        count: 231,
        value: 'Barclays',
      },
      {
        pinned: true,
        label: 'Investec Structured Products',
        count: 1353,
        value: 'Investec Structured Products',
      },
      {
        pinned: true,
        label: 'Meteor',
        count: 1366,
        value: 'Meteor',
      },
      {
        pinned: false,
        label: 'Goldman Sachs',
        count: 48,
        value: 'Goldman Sachs',
      },
      {
        pinned: false,
        label: 'Morgan Stanley',
        count: 176,
        value: 'Morgan Stanley',
      },
      {
        pinned: false,
        label: 'Societe Generale',
        count: 308,
        value: 'Societe Generale',
      },
      {
        pinned: false,
        label: 'Walker Crips',
        count: 1441,
        value: 'Walker Crips',
      },
    ],
  },
  {
    categoryLabel: 'Counterparty',
    categoryKey: 'counterparty',
    items: [
      {
        pinned: true,
        label: 'Barclays Bank plc',
        count: 839,
        value: 2,
      },
      {
        pinned: true,
        label: 'Citigroup Global Markets Limited',
        count: 440,
        value: 1,
      },
      {
        pinned: true,
        label: 'HSBC Bank plc',
        count: 753,
        value: 6,
      },
      {
        pinned: false,
        label: 'Morgan Stanley & Co. International plc',
        count: 514,
        value: 4,
      },
      {
        pinned: false,
        label: 'Societe Generale',
        count: 484,
        value: 7,
      },
      {
        pinned: false,
        label: 'Investec Bank plc',
        count: 661,
        value: 25,
      },
    ],
  },
  {
    categoryLabel: 'Credit Rating Bands',
    categoryKey: 'creditratingbands',
    items: [
      {
        pinned: true,
        label: 'A band',
        count: 5176,
        value: 3,
      },
      {
        pinned: true,
        label: 'AA band',
        count: 813,
        value: 2,
      },
      {
        pinned: false,
        label: 'BBB band',
        count: 366,
        value: 4,
      },
      {
        pinned: false,
        label: 'No rating',
        count: 12,
        value: 99,
      },
    ],
  },
]

export default {
  name: 'FullDatabaseStatic',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardTitle,
    BCardText,
    BTable,
    BAvatar,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BPagination,
    BFormSelect,
    ProductFilter,
    Swiper,
    SwiperSlide,
  },
  data() {
    const rows = NAMES.map((name, i) => {
      const type = TYPES[i % TYPES.length]
      return {
        productid: `${1000 + i * 137}`,
        producttype: type.label,
        iconName: type.icon,
        iconColor: type.color,
        productname: `${PROVIDERS[i % PROVIDERS.length]} ${name}`,
        strike: fakeDate(0, i),
        final: fakeDate(3 + (i % 3), i % 6),
      }
    })

    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      totalRows: 7951,
      filterDisplay: FILTER_DISPLAY,
      recentlyViewed: RECENTLY_VIEWED,
      swiperOptions: {
        slidesPerView: 7,
        spaceBetween: 12,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          320: { slidesPerView: 2, spaceBetween: 8 },
          768: { slidesPerView: 3, spaceBetween: 10 },
          1024: { slidesPerView: 5, spaceBetween: 12 },
          1440: { slidesPerView: 7, spaceBetween: 12 },
        },
      },
      fields: [
        { key: 'productid', label: 'Id', thStyle: { width: '8%' } },
        { key: 'producttype', label: 'Type', thStyle: { width: '16%' } },
        { key: 'productname', label: 'Name' },
        { key: 'strike', label: 'Strike Date', thStyle: { width: '13%' } },
        { key: 'final', label: 'Final Date', thStyle: { width: '13%' } },
        { key: 'portfolio', label: '', thStyle: { width: '5%' } },
      ],
      rows,
    }
  },
}
</script>

<style scoped>
.swiper-container {
  position: relative;
  padding: 0 50px;
}

.swiper-button-next,
.swiper-button-prev {
  color: #1a73e8;
}

.swiper-button-next:after,
.swiper-button-prev:after {
  font-size: 24px;
  font-weight: bold;
}

.swiper-slide .card-body {
  padding: 1rem 0.5rem 0.25rem 0.5rem;
  text-align: center;
}

.swiper-slide .card-title {
  font-size: 1.1rem;
  margin-bottom: 0.35rem !important;
  margin-top: 0.2rem !important;
}

.swiper-slide .card-text {
  font-size: 0.75rem;
  margin-bottom: 0.25rem !important;
}

.swiper-slide .card-img-top {
  width: 100%;
  height: 100px;
  object-fit: contain;
  border-top-left-radius: 0.357rem;
  border-top-right-radius: 0.357rem;
}

</style>
